// MUI-free copy of Ui-components/CommonSections/FAQ. Answers stay in the server HTML (collapsed panels are hidden,
// not unmounted, as with MUI's Accordion). Like the original, this component emits no FAQPage JSON-LD.
import {memo} from 'react';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import AccordionItem from '@/components/atoms/AccordionItem';
import {useOpenItem} from '@/hooks/useOpenItem';
import styles from './FAQ.module.scss';

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface FAQItem {
    q: string;
    a: string;
}

export interface FAQCategory {
    category?: string;
    questions: FAQItem[];
}

interface FAQProps {
    heading?: string;
    faqData: FAQCategory[];
}

const cx = (...names: Array<string | false | undefined>) => names.filter(Boolean).join(' ');

// ─── Main component ──────────────────────────────────────────────────────────────

const FAQ = ({heading, faqData}: FAQProps) => {
    const allQuestions = faqData?.flatMap((category) => category.questions) || [];
    const [expanded, handleToggle] = useOpenItem();

    const half = Math.ceil(allQuestions.length / 2);
    const leftCol = allQuestions.slice(0, half);
    const rightCol = allQuestions.slice(half);

    return (
        <div>
            <Container>
                {heading && <h2 className={cx('font-headers', styles.heading)}>{parse(heading)}</h2>}

                <div className={styles.grid}>
                    <div className={styles.item}>
                        {leftCol.map((item, idx) => (
                            <div key={idx} className={styles.itemWrap}>
                                <AccordionItem
                                    question={item.q}
                                    answer={item.a}
                                    expanded={expanded === idx}
                                    onToggle={() => handleToggle(idx)}
                                />
                            </div>
                        ))}
                    </div>
                    <div className={styles.item}>
                        {rightCol.map((item, idx) => {
                            const actualIdx = idx + half;
                            return (
                                <div key={actualIdx} className={styles.itemWrap}>
                                    <AccordionItem
                                        question={item.q}
                                        answer={item.a}
                                        expanded={expanded === actualIdx}
                                        onToggle={() => handleToggle(actualIdx)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default memo(FAQ);
